import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../utils/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Student } from '../../../utils/firebase';
import {
  SEAT_STUDENT_CELL_PX,
  SEAT_STUDENT_CHIP_PX,
  SEAT_STUDENT_CHIP_BORDER_RADIUS_PX,
  studentSeatBoardPx as seatBoardPx
} from '../../../utils/seatBoardLayout';
import Intersect from '../../../components/icons/Intersect';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';

/** 교사 '예약 자리 관리'에서 편집·게시하는 자리표 (SeatManagement의 reservation 모드와 동일 문서) */
const SEATING_ARRANGEMENT_RESERVATION_ID = 'reservation';

interface SeatInfo {
  uid?: string;
  studentId?: string | null;
  name?: string;
  studentName?: string | null;
  position: { row: number; col: number };
  isDisabled?: boolean;
  isReserved?: boolean;
  reservedAt?: Date;
  seatTax?: number | null;
  groupId?: string;
  bidAmount?: number | null;
  bidTime?: Date | null;
}

interface SeatingSettings {
  rows: number;
  cols: number;
  isPairedSeating: boolean;
  boardPosition: 'top' | 'bottom';
  layoutTemplate?: string;
}

interface SeatingArrangement {
  id: string;
  classId: string;
  schoolId: string;
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  status: 'draft' | 'published' | 'reservation' | 'normal';
  seats: Record<string, SeatInfo>;
  settings: SeatingSettings;
  reservationSettings: {
    enabled: boolean;
    startDate: Date;
    endDate: Date;
  };
}

const firestoreDate = (v: unknown, fallback: Date): Date => {
  if (v != null && typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  if (v instanceof Date) return v;
  return fallback;
};

const normalizeSettings = (raw: Record<string, unknown> | undefined): SeatingSettings => {
  const s = raw || {};
  const rows = typeof s.rows === 'number' ? s.rows : 5;
  const cols = typeof s.cols === 'number' ? s.cols : 6;
  const isPaired =
    typeof s.isPairedSeating === 'boolean'
      ? s.isPairedSeating
      : typeof s.isPair === 'boolean'
        ? s.isPair
        : false;
  const boardPos = s.boardPosition === 'bottom' ? 'bottom' : 'top';
  const layoutTemplate = typeof s.layoutTemplate === 'string' ? s.layoutTemplate : undefined;
  return {
    rows,
    cols,
    isPairedSeating: isPaired,
    boardPosition: boardPos,
    layoutTemplate
  };
};

const isSeatOccupied = (seat: SeatInfo | undefined): boolean => {
  if (!seat) return false;
  if (seat.isReserved) return true;
  const id = seat.uid ?? seat.studentId;
  if (id != null && String(id).trim() !== '') return true;
  return false;
};

const getSeatOccupantId = (seat: SeatInfo | undefined): string | null => {
  if (!seat) return null;
  const id = seat.uid ?? seat.studentId;
  if (id == null || String(id).trim() === '') return null;
  return String(id).trim();
};

const getSeatDisplayName = (seat: SeatInfo | undefined): string | null => {
  if (!seat) return null;
  const n = seat.name ?? seat.studentName;
  if (n == null || String(n).trim() === '') return null;
  return String(n).trim();
};

/** 학생이 예약 아이템으로 잡은 자리(isReserved + 본인 uid) */
const isOwnReservedSeat = (seat: SeatInfo | undefined, studentUid: string): boolean => {
  if (!seat || seat.isReserved !== true) return false;
  const id = String(seat.uid ?? seat.studentId ?? '').trim();
  return Boolean(studentUid) && id === studentUid;
};

const getOwnReservedSeatKeys = (seats: Record<string, SeatInfo>, studentUid: string): string[] =>
  Object.entries(seats)
    .filter(([, s]) => isOwnReservedSeat(s, studentUid))
    .map(([k]) => k);

/** 예약 취소 후 교사 자리표와 맞는 빈 칸 형태 */
const buildClearedSeatPayload = (row: number, col: number, previous: SeatInfo | undefined): Record<string, unknown> => ({
  studentId: null,
  studentName: null,
  isDisabled: Boolean(previous?.isDisabled),
  position: { row, col },
  bidAmount: null,
  bidTime: null,
  seatTax: previous?.seatTax ?? null,
  ...(previous?.groupId ? { groupId: previous.groupId } : {})
});

const SeatReservation: React.FC = () => {
  const { currentUser } = useAuth();
  const student = currentUser as Student | null;
  const [seatingArrangement, setSeatingArrangement] = useState<SeatingArrangement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeat, setSelectedSeat] = useState<{ row: number; col: number } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [reservationItems, setReservationItems] = useState(0);
  const [studentNumberByUid, setStudentNumberByUid] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    const fetchData = async () => {
      if (!student?.uid || !student.schoolId || !student.classId) {
        setError('학생/학급 정보를 확인할 수 없습니다.');
        setLoading(false);
        return;
      }

      try {
        const arrangementRef = doc(
          db,
          `schools/${student.schoolId}/classes/${student.classId}/seatingArrangements`,
          SEATING_ARRANGEMENT_RESERVATION_ID
        );
        const arrangementDoc = await getDoc(arrangementRef);

        if (!arrangementDoc.exists()) {
          setError('예약용 자리 배치가 아직 없습니다. 담임 선생님께 예약 자리 관리 시작을 요청해 주세요.');
          setLoading(false);
          return;
        }

        const data = arrangementDoc.data();
        const settingsRaw = data.settings as Record<string, unknown> | undefined;
        const periodStart = firestoreDate(
          settingsRaw?.startDate ?? settingsRaw?.reservationStartDate ?? data.startDate,
          new Date()
        );
        const periodEnd = firestoreDate(
          settingsRaw?.endDate ?? settingsRaw?.reservationEndDate ?? data.endDate,
          new Date()
        );
        setSeatingArrangement({
          id: arrangementDoc.id,
          classId: student.classId,
          schoolId: student.schoolId,
          startDate: periodStart,
          endDate: periodEnd,
          isActive: data.isActive || false,
          status: (data.status as SeatingArrangement['status']) || 'draft',
          seats: data.seats || {},
          settings: normalizeSettings(settingsRaw),
          reservationSettings: data.reservationSettings || {
            enabled: Boolean(settingsRaw?.enabled),
            startDate: periodStart,
            endDate: periodEnd
          }
        });

        const studentRef = doc(db, `schools/${student.schoolId}/classes/${student.classId}/students`, student.uid);
        const studentDoc = await getDoc(studentRef);
        if (studentDoc.exists()) {
          const sd = studentDoc.data();
          let ri = Number(sd.reservationItems || 0);
          if (sd.reservationItemsFromStoreSynced !== true && student.schoolId && student.classId) {
            try {
              const functions = getFunctions(getApp());
              const sync = httpsCallable<{ schoolId: string; classId: string }, { reservationItems?: number }>(
                functions,
                'syncSeatReservationItems'
              );
              const synced = await sync({ schoolId: student.schoolId, classId: student.classId });
              ri = Number(synced.data.reservationItems ?? ri);
            } catch (syncErr) {
              console.error('예약 아이템 동기화 실패:', syncErr);
            }
          }
          setReservationItems(ri);
        }

        const studentsRef = collection(db, `schools/${student.schoolId}/classes/${student.classId}/students`);
        const studentsSnap = await getDocs(studentsRef);
        const map = new Map<string, number>();
        studentsSnap.docs.forEach((d) => {
          const raw = d.data().number as unknown;
          const num =
            raw === null || raw === undefined || raw === ''
              ? NaN
              : typeof raw === 'number'
                ? raw
                : Number(raw);
          if (Number.isFinite(num) && num > 0) {
            map.set(String(d.id).trim(), Math.floor(num));
          }
        });
        setStudentNumberByUid(map);
      } catch (err) {
        console.error('Error fetching seat reservation data:', err);
        setError('좌석 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [student]);

  const getSeatPositionLabel = (row: number, col: number, seat: SeatInfo | undefined) => {
    const seatCode = String.fromCharCode(65 + row) + String(col + 1).padStart(2, '0');
    const occupantId = getSeatOccupantId(seat);
    if (!occupantId) return seatCode;
    const num = studentNumberByUid.get(occupantId);
    if (num != null && num > 0) {
      return `${num}번`;
    }
    return seatCode;
  };

  const seatPositionLabelStyle: React.CSSProperties = {
    fontSize: `${seatBoardPx(14)}px`,
    fontWeight: 600,
    fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
    lineHeight: `${seatBoardPx(18)}px`,
    textAlign: 'center',
    minWidth: `${seatBoardPx(44)}px`,
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
    display: 'inline-block',
    boxSizing: 'border-box'
  };

  const handleSeatSelect = (row: number, col: number) => {
    if (!seatingArrangement || !student?.uid) return;
    const seatKey = `${row}-${col}`;
    const seat = seatingArrangement.seats[seatKey];
    if (seat?.isDisabled) return;
    if (isSeatOccupied(seat)) return;

    const ownKeys = getOwnReservedSeatKeys(seatingArrangement.seats, student.uid);
    const isChangingReservation = ownKeys.length > 0 && !ownKeys.includes(seatKey);

    /** 첫 예약이든 자리 변경이든 좌석을 잡을 때마다 예약 아이템 1개 필요 */
    if (reservationItems <= 0) {
      setError(
        isChangingReservation
          ? '자리를 바꾸려면 예약 아이템이 1개 이상 필요합니다.'
          : '예약 아이템이 부족합니다. 새 자리를 예약하려면 예약권이 필요합니다.'
      );
      return;
    }

    setError(null);
    setSelectedSeat({ row, col });
    setConfirmDialog(true);
  };

  const handleConfirmReservation = async () => {
    if (!selectedSeat || !seatingArrangement || !student?.uid || !student.schoolId || !student.classId) return;

    const seatKey = `${selectedSeat.row}-${selectedSeat.col}`;

    try {
      const functions = getFunctions(getApp());
      const reserve = httpsCallable<
        { schoolId: string; classId: string; row: number; col: number },
        { reservationItems?: number; clearedKeys?: string[]; seatKey?: string }
      >(functions, 'reserveClassSeat');
      const reserved = await reserve({
        schoolId: student.schoolId,
        classId: student.classId,
        row: selectedSeat.row,
        col: selectedSeat.col,
      });
      const clearedKeysOut = reserved.data.clearedKeys || [];

      setSeatingArrangement((prev) => {
        if (!prev) return prev;
        const nextSeats = { ...prev.seats };
        for (const k of clearedKeysOut) {
          const [r, c] = k.split('-').map(Number);
          nextSeats[k] = buildClearedSeatPayload(r, c, prev.seats[k]) as unknown as SeatInfo;
        }
        nextSeats[seatKey] = {
          uid: student.uid,
          name: student.name,
          position: selectedSeat,
          isReserved: true,
          reservedAt: new Date()
        };
        return { ...prev, seats: nextSeats };
      });
      setReservationItems(Number(reserved.data.reservationItems ?? 0));
      setSelectedSeat(null);
      setConfirmDialog(false);
    } catch (err) {
      console.error('Error reserving seat:', err);
      setError(err instanceof Error ? err.message : '좌석 예약 중 오류가 발생했습니다.');
    }
  };

  const renderBoardSection = () => (
    <div
      className="seat-management-board-section"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '20px',
        marginBottom: '12px'
      }}
    >
      <div
        className="seat-management-board-label"
        style={{
          width: '100%',
          textAlign: 'center',
          color: 'var(--Color-text-minor, #BABABA)',
          fontSize: '14px',
          fontFamily: 'var(--font-family-primary, Pretendard)',
          fontWeight: 600,
          lineHeight: '21px',
          wordWrap: 'break-word'
        }}
      >
        칠판
      </div>
      <div
        className="seat-management-board-icon"
        style={{
          width: '100%',
          maxWidth: '500px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Intersect width={seatBoardPx(500)} height={seatBoardPx(38)} />
      </div>
    </div>
  );

  const renderSeatGrid = () => {
    if (!seatingArrangement) return null;
    const { rows, cols } = seatingArrangement.settings;
    const CELL_SIZE = SEAT_STUDENT_CELL_PX;

    /** 교사가 자리를 만들지 않았거나(isAbsent), 비활성화한 자리는 칩을 그리지 않음. 그리드 정렬만 동일 크기 빈 칸으로 유지 */
    const shouldShowSeatChip = (seat: SeatInfo | undefined): boolean =>
      Boolean(seat) && !seat?.isDisabled;

    return (
      <div
        className="seat-management-grid-section"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          overflow: 'auto'
        }}
      >
        <div
          style={{
            width: 'auto',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0px'
          }}
        >
          {Array.from({ length: rows }).map((_, row) => (
            <div
              key={`grid-row-${row}`}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {Array.from({ length: cols }).map((_, col) => {
                const seatKey = `${row}-${col}`;
                const seat = seatingArrangement.seats[seatKey];

                if (!shouldShowSeatChip(seat)) {
                  return (
                    <div
                      key={seatKey}
                      aria-hidden
                      style={{
                        width: `${CELL_SIZE}px`,
                        height: `${CELL_SIZE}px`,
                        flexShrink: 0,
                        boxSizing: 'border-box'
                      }}
                    />
                  );
                }

                const occupied = isSeatOccupied(seat);
                const displayName = getSeatDisplayName(seat);
                const isSelected = selectedSeat?.row === row && selectedSeat?.col === col;
                const canClick = !occupied;

                let chipBg = '#F8F8F8';
                if (occupied) chipBg = '#FFF1E7';
                else if (isSelected) chipBg = '#FFE5D3';

                const cursor = occupied ? 'not-allowed' : 'pointer';

                return (
                  <div
                    key={seatKey}
                    role="button"
                    tabIndex={canClick ? 0 : -1}
                    aria-disabled={!canClick}
                    onClick={() => handleSeatSelect(row, col)}
                    onKeyDown={(e) => {
                      if (!canClick) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSeatSelect(row, col);
                      }
                    }}
                    style={{
                      width: `${CELL_SIZE}px`,
                      height: `${CELL_SIZE}px`,
                      border: 'none',
                      background: 'transparent',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div
                      style={{
                        width: `${SEAT_STUDENT_CHIP_PX}px`,
                        height: `${SEAT_STUDENT_CHIP_PX}px`,
                        background: chipBg,
                        borderRadius: `${SEAT_STUDENT_CHIP_BORDER_RADIUS_PX}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor,
                        boxShadow: isSelected && canClick ? '0px 2px 8px rgba(255, 111, 15, 0.35)' : 'none'
                      }}
                    >
                      <div
                        style={{
                          ...seatPositionLabelStyle,
                          color: '#4C4C4C'
                        }}
                      >
                        {getSeatPositionLabel(row, col, seat)}
                      </div>
                      {displayName && (
                        <div
                          style={{
                            fontSize: `${seatBoardPx(13)}px`,
                            color: '#4C4C4C',
                            fontFamily: 'var(--font-family-primary, Pretendard)',
                            fontWeight: 500,
                            marginTop: '1px',
                            lineHeight: `${seatBoardPx(17)}px`,
                            textAlign: 'center',
                            maxWidth: `${seatBoardPx(68)}px`,
                            wordBreak: 'keep-all'
                          }}
                        >
                          {displayName}
                        </div>
                      )}
                      {seat?.seatTax != null && seat.seatTax !== undefined && (
                        <div
                          style={{
                            fontSize: `${seatBoardPx(9)}px`,
                            color: 'var(--Color-fill-brand-color, #FF6F0F)',
                            fontFamily: 'var(--font-family-primary, Pretendard)',
                            fontWeight: 600,
                            marginTop: '1px',
                            lineHeight: `${seatBoardPx(12)}px`,
                            textAlign: 'center'
                          }}
                        >
                          자리세 {seat.seatTax.toLocaleString('ko-KR')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ fontFamily: 'var(--font-family-primary, Pretendard)', color: 'var(--Color-text-assistive, #989898)' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  if (error && !seatingArrangement) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div
          style={{
            color: 'var(--Color-status-negative, #842029)',
            background: '#f8d7da',
            border: '1px solid #f5c2c7',
            padding: '12px 16px',
            borderRadius: 8,
            fontFamily: 'var(--font-family-primary, Pretendard)'
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  if (!seatingArrangement) return null;

  const confirmSelectedKey =
    confirmDialog && selectedSeat ? `${selectedSeat.row}-${selectedSeat.col}` : '';
  const ownKeysForDialog =
    confirmDialog && student?.uid ? getOwnReservedSeatKeys(seatingArrangement.seats, student.uid) : [];
  const isChangingReservationDialog =
    Boolean(confirmSelectedKey) &&
    ownKeysForDialog.length > 0 &&
    !ownKeysForDialog.includes(confirmSelectedKey);

  const mainColumn = (
    <>
      {renderBoardSection()}
      {renderSeatGrid()}
    </>
  );

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gap-level-4, 16px)',
        fontFamily: 'var(--font-family-primary, Pretendard)'
      }}
    >
      {error && (
        <div
          style={{
            color: 'var(--Color-status-negative, #842029)',
            background: '#f8d7da',
            border: '1px solid #f5c2c7',
            padding: '12px 16px',
            borderRadius: 8
          }}
        >
          {error}
        </div>
      )}

      <div>
        <h1 style={{ margin: 0, fontSize: 32, color: 'var(--Color-text-normal, #161616)', fontWeight: 600 }}>좌석 예약</h1>
        <p style={{ margin: '8px 0 0', color: 'var(--Color-text-assistive, #989898)', fontSize: 14 }}>
          기간: {seatingArrangement.startDate.toLocaleDateString()} ~ {seatingArrangement.endDate.toLocaleDateString()}
        </p>
        <p style={{ margin: '4px 0 0', color: 'var(--Color-fill-brand-color, #FF6F0F)', fontWeight: 600, fontSize: 14 }}>
          남은 예약 아이템: {reservationItems}개
        </p>
      </div>

      <Card width="100%" padding="0" borderRadius="16px" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--Color-text-normal, #161616)' }}>자리 배치</h2>
        </div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: seatingArrangement.settings.boardPosition === 'bottom' ? 'column-reverse' : 'column',
            alignItems: 'center',
            minHeight: 0
          }}
        >
          {mainColumn}
        </div>
      </Card>

      {confirmDialog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16
          }}
        >
          <Card width="100%" padding="20px" borderRadius="12px" style={{ maxWidth: 420 }}>
            <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 18, fontWeight: 600 }}>좌석 예약 확인</h3>
            <p style={{ marginBottom: 20, color: 'var(--Color-text-assistive, #989898)', fontSize: 14, lineHeight: '21px' }}>
              {isChangingReservationDialog
                ? '이미 예약한 자리는 취소되고, 선택한 자리로 바뀝니다. 자리를 바꿀 때마다 예약 아이템 1개가 소모됩니다.'
                : '선택한 좌석을 예약하시겠습니까? 예약 아이템 1개가 소모됩니다.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button variant="secondary" size="medium" onClick={() => setConfirmDialog(false)}>
                취소
              </Button>
              <Button variant="primary" size="medium" onClick={handleConfirmReservation}>
                {isChangingReservationDialog ? '자리 바꾸기' : '예약하기'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SeatReservation;
